import { isDropInHistory } from "@/features/history/isDropInHistory";
import { type AppointmentStatus, type History, type Task } from "@repo/api";
import { t } from "@repo/translation";
import {
  type TaskConfig,
  localizeDate,
  localizeDateAndTime,
  localizeDateTimeRange,
  translateAppointmentStatusChange,
  translateAsKey,
  translateLabel,
  translateTaskType,
} from "@repo/ui";
import { combineDates, formatDateToApiUTC } from "@repo/utils";
import { type Locale, addMinutes, endOfDay } from "date-fns";
import uniq from "lodash/uniq";
import { type ReactElement } from "react";

import { type TaskModalState } from "./taskModal/TaskModalReducer";

export const getTaskUpdateDetail = (
  language: string,
  entry?: History,
  dateFnsLocale?: Locale,
  taskConfigs?: TaskConfig[]
): { title?: string; description?: (string | ReactElement)[] } | undefined => {
  const isDropIn = entry && isDropInHistory(entry);
  const updates = entry?.details?.updates;
  if (!updates) {
    return undefined;
  }

  let title;
  const description = [] as (string | ReactElement)[];
  updates.forEach((update) => {
    // task complete/unchecked
    if (update.field === "isDone" && !isDropIn) {
      if (update.old_value === "false" && update.new_value === "true") {
        const prompt = isDropIn
          ? t("prompt.appointment_completed")
          : t("prompt.task_completed");
        title = prompt;
        description.unshift(prompt);
      }

      if (update.old_value === "true" && update.new_value === "false") {
        title = t("prompt.task_unchecked");
        description.unshift(t("prompt.task_unchecked"));
      }
    }

    // update task title
    if (update.field === "title") {
      if (update.old_value && update.new_value) {
        description.unshift(
          t("prompt.title_updated_from_to", {
            old: update.old_value,
            new: update.new_value,
          })
        );
      }
    }

    // update task time
    if (
      update.field === "taskStartDateTime" ||
      update.field === "taskEndDateTime"
    ) {
      const startTime = updates.find((el) => el.field === "taskStartDateTime");
      const endTime = updates.find((el) => el.field === "taskEndDateTime");
      const isTimeSet = updates.find((el) => el.field === "isTimeSet");
      const isAllDay = (from: string, to: string) =>
        from.includes("00:00") && to.includes("59:59");

      if (startTime && endTime) {
        description.unshift(
          t("prompt.time_updated_from_to", {
            old:
              isTimeSet?.old_value === "false" ||
              isAllDay(startTime.old_value, endTime.old_value)
                ? localizeDate(startTime.old_value, dateFnsLocale)
                : localizeDateTimeRange(
                    startTime.old_value,
                    endTime.old_value,
                    dateFnsLocale
                  ),
            new:
              isTimeSet?.new_value === "false" ||
              isAllDay(startTime.new_value, endTime.new_value)
                ? localizeDate(startTime.new_value, dateFnsLocale)
                : localizeDateTimeRange(
                    startTime.new_value,
                    endTime.new_value,
                    dateFnsLocale
                  ),
          })
        );
      } else if (startTime && !endTime) {
        description.unshift(
          t("prompt.start_time_updated_from_to", {
            old: localizeDateAndTime(startTime.old_value, dateFnsLocale),
            new: localizeDateAndTime(startTime.new_value, dateFnsLocale),
          })
        );
      } else if (!startTime && endTime) {
        description.unshift(
          t("prompt.end_time_updated_from_to", {
            old: localizeDateAndTime(endTime.old_value, dateFnsLocale),
            new: localizeDateAndTime(endTime.new_value, dateFnsLocale),
          })
        );
      }
    }

    // reassign
    if (update.field === "emailId") {
      description.unshift(
        t("prompt.reassigned_from_to", {
          old: update.old_value,
          new: update.new_value,
        })
      );
    }

    // comment
    if (update.field === "description") {
      description.push(
        t("prompt.comment_updated_from_to", {
          old: update.old_value,
          new: update.new_value,
        })
      );
    }

    if (update.field === "parentTaskType") {
      description.push(
        t("prompt.type_updated_from_to", {
          old: translateTaskType(
            language,
            update.old_value,
            undefined,
            taskConfigs
          ),
          new: translateTaskType(
            language,
            update.new_value,
            undefined,
            taskConfigs
          ),
        })
      );
    }

    if (update.field === "subTaskType") {
      description.push(
        t("prompt.subtype_updated_from_to", {
          old: translateTaskType(
            language,
            entry.details?.parent_task_type,
            update.old_value,
            taskConfigs
          ),
          new: translateTaskType(
            language,
            entry.details?.parent_task_type,
            update.old_value,
            taskConfigs
          ),
        })
      );
    }
    // update appointmentStatus
    if (update.field === "appointmentStatus" && update.new_value) {
      description.push(
        translateAppointmentStatusChange(update.new_value as AppointmentStatus)
      );
    }
  });

  // set default title
  if (!title) {
    title = isDropIn ? t("prompt.appointment_updated") : entry.title;
  }

  // dedup (ex. start time update and end time update will appear twice)
  return { title, description: uniq(description) };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const shouldAllowActions = (task?: Task) => {
  return true;
};

export const calculateEndDate = ({
  allDay,
  endDate,
  endTime,
  startDate,
  startTime,
}: {
  allDay: boolean;
  endDate: Date | undefined;
  endTime: Date | undefined;
  startDate: Date | undefined;
  startTime: Date | undefined;
}) => {
  if (endDate && endTime) {
    return combineDates(endDate, endTime);
  }

  if (allDay && endDate) {
    return endOfDay(endDate);
  }

  if (startDate && startTime) {
    return addMinutes(combineDates(startDate, startTime), 30);
  }

  return undefined;
};

export const getTaskDueDate = (task: Task) => {
  if (task.isTaskDueBy && task.taskDate) {
    return new Date(task.taskDate);
  }

  return undefined;
};

export const getTaskDates = (
  state: TaskModalState,
  calculatedEndDate: Date | undefined
) => {
  if (state.isDueDateTask && state.dueDate) {
    return {
      isTaskDueBy: true,
      taskDate: formatDateToApiUTC(combineDates(state.dueDate, state.dueTime)),
    };
  } else {
    return {
      isTaskDueBy: false,
      taskStartDateTime: state.startDate
        ? state.allDay
          ? formatDateToApiUTC(combineDates(state.startDate, state.startTime))
          : formatDateToApiUTC(combineDates(state.startDate, state.startTime))
        : undefined,

      taskEndDateTime: calculatedEndDate
        ? state.allDay
          ? formatDateToApiUTC(calculatedEndDate)
          : formatDateToApiUTC(calculatedEndDate)
        : undefined,
    };
  }
};

export const getTaskTitle = (task?: Task | TaskModalState) => {
  return task?.defaultTitle
    ? translateAsKey(task.title)
    : translateLabel(task?.title);
};
